// =============================================================================
// Growth Dashboard — AI-powered rubric assessment across 7 areas
// =============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { RUBRIC_AREAS } from '../constants/rubric';

interface GrowthDashboardProps {
  reportId: string;
  reportYear: string;
}

interface AreaAssessment {
  areaId: string;
  suggestedRating: number;
  level: string;
  reasoning: string;
  growthAreas: string[];
  suggestions: { action: string; timeline: string; why: string }[];
  trajectory: string;
}

interface AssessmentResult {
  areas: AreaAssessment[];
  overallSummary: string;
  disclaimer: string;
  error?: string;
}

// Styles
const cardStyle: React.CSSProperties = {
  padding: '24px 28px', borderRadius: 22,
  background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(12px)',
  border: '1px solid rgba(180,160,210,0.15)',
  boxShadow: '0 4px 24px rgba(100,60,140,0.06)',
};

const btnPrimary: React.CSSProperties = {
  padding: '14px 32px', borderRadius: 22, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #7c4dba, #9b72cf)', color: '#fff',
  fontSize: 15, fontWeight: 600, transition: 'all 0.3s',
  boxShadow: '0 4px 16px rgba(124,77,186,0.3)',
  display: 'flex', alignItems: 'center', gap: 8,
};

// Radar chart drawing
function drawRadarChart(
  canvas: HTMLCanvasElement,
  areas: typeof RUBRIC_AREAS,
  ratings: number[],
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const size = 340;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  ctx.scale(dpr, dpr);

  const cx = size / 2, cy = size / 2, r = 130;
  const n = areas.length;
  const angleStep = (2 * Math.PI) / n;
  const startAngle = -Math.PI / 2;

  ctx.clearRect(0, 0, size, size);

  // Draw concentric rings
  for (let ring = 1; ring <= 5; ring++) {
    const rr = (ring / 5) * r;
    ctx.beginPath();
    for (let i = 0; i <= n; i++) {
      const a = startAngle + i * angleStep;
      const x = cx + rr * Math.cos(a);
      const y = cy + rr * Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.strokeStyle = ring === 2 ? 'rgba(124,77,186,0.08)' : ring === 4 ? 'rgba(124,77,186,0.08)' : 'rgba(180,160,210,0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Ring labels
    if (ring === 2 || ring === 4 || ring === 5) {
      ctx.fillStyle = 'rgba(160,140,180,0.5)';
      ctx.font = '9px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText(`${ring}`, cx + rr * Math.cos(startAngle) + 0, cy + rr * Math.sin(startAngle) - 4);
    }
  }

  // Draw axis lines + labels
  for (let i = 0; i < n; i++) {
    const a = startAngle + i * angleStep;
    const ex = cx + r * Math.cos(a);
    const ey = cy + r * Math.sin(a);

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(ex, ey);
    ctx.strokeStyle = 'rgba(180,160,210,0.15)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Label
    const labelR = r + 22;
    const lx = cx + labelR * Math.cos(a);
    const ly = cy + labelR * Math.sin(a);
    ctx.fillStyle = areas[i].color;
    ctx.font = 'bold 10px system-ui';
    ctx.textAlign = Math.cos(a) < -0.1 ? 'right' : Math.cos(a) > 0.1 ? 'left' : 'center';
    ctx.textBaseline = Math.sin(a) < -0.1 ? 'bottom' : Math.sin(a) > 0.1 ? 'top' : 'middle';

    // Wrap long names
    const name = areas[i].name;
    if (name.length > 14) {
      const mid = name.lastIndexOf(' ', Math.floor(name.length / 2));
      if (mid > 0) {
        ctx.fillText(name.slice(0, mid), lx, ly - 6);
        ctx.fillText(name.slice(mid + 1), lx, ly + 6);
      } else {
        ctx.fillText(name, lx, ly);
      }
    } else {
      ctx.fillText(name, lx, ly);
    }
  }

  // Draw filled polygon for ratings
  if (ratings.length === n && ratings.some(v => v > 0)) {
    ctx.beginPath();
    for (let i = 0; i <= n; i++) {
      const idx = i % n;
      const a = startAngle + idx * angleStep;
      const v = (ratings[idx] / 5) * r;
      const x = cx + v * Math.cos(a);
      const y = cy + v * Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();

    // Gradient fill
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, 'rgba(124,77,186,0.15)');
    grad.addColorStop(1, 'rgba(124,77,186,0.05)');
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.strokeStyle = 'rgba(124,77,186,0.6)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Dots
    for (let i = 0; i < n; i++) {
      const a = startAngle + i * angleStep;
      const v = (ratings[i] / 5) * r;
      const x = cx + v * Math.cos(a);
      const y = cy + v * Math.sin(a);
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      ctx.fillStyle = areas[i].color;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }
}

function levelLabel(rating: number, levels: string[]): string {
  if (rating <= 2) return levels[0];
  if (rating <= 4) return levels[1];
  return levels[2];
}

function ratingColor(rating: number): string {
  if (rating <= 2) return '#d97706';
  if (rating <= 4) return '#2563eb';
  return '#059669';
}

// ============================================================================
// Main Component
// ============================================================================

export default function GrowthDashboard({ reportId, reportYear }: GrowthDashboardProps) {
  const [assessment, setAssessment] = useState<AssessmentResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCached, setLoadingCached] = useState(true);
  const [isCached, setIsCached] = useState(false);
  const [error, setError] = useState('');
  const [expandedArea, setExpandedArea] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Draw radar chart when assessment arrives
  useEffect(() => {
    if (!assessment?.areas?.length || !canvasRef.current) return;
    const ratings = RUBRIC_AREAS.map(ra => {
      const found = assessment.areas.find(a => a.areaId === ra.id);
      return found?.suggestedRating || 0;
    });
    drawRadarChart(canvasRef.current, RUBRIC_AREAS, ratings);
  }, [assessment]);

  // Load cached assessment on mount
  useEffect(() => {
    const loadCached = async () => {
      try {
        const resp = await fetch(`/api/ai/rubric-assessment/${reportId}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        if (resp.ok) {
          const result = await resp.json();
          if (result.cached) {
            setAssessment(result);
            setIsCached(true);
          }
        }
      } catch {
        // Silently fail — user can still generate manually
      } finally {
        setLoadingCached(false);
      }
    };
    loadCached();
  }, [reportId]);

  const handleRunAssessment = async (force = false) => {
    setLoading(true);
    setError('');
    setAssessment(null);
    setIsCached(false);
    try {
      const url = force
        ? `/api/ai/rubric-assessment/${reportId}?force=true`
        : `/api/ai/rubric-assessment/${reportId}`;
      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${resp.status}`);
      }
      const result = await resp.json();
      setAssessment(result);
      setIsCached(!!result.cached);
    } catch (e: any) {
      setError(e.message || 'Failed to generate assessment');
    } finally {
      setLoading(false);
    }
  };

  // PDF generation
  const handleDownloadPDF = useCallback(async () => {
    if (!assessment) return;
    setPdfGenerating(true);
    try {
      // Build HTML content for the PDF
      const areas = assessment.areas || [];
      const areasHtml = areas.map(area => {
        const ra = RUBRIC_AREAS.find(r => r.id === area.areaId);
        const name = ra?.name || area.areaId;
        const color = ra?.color || '#7c4dba';
        return `
          <div style="page-break-inside: avoid; margin-bottom: 24px; padding: 18px 20px; border-radius: 12px; border-left: 4px solid ${color}; background: #faf8ff;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <h3 style="margin: 0; color: #3a2a50; font-size: 16px;">${name}</h3>
              <div style="text-align: center;">
                <div style="width: 40px; height: 40px; border-radius: 10px; background: ${color}18; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: 700; color: ${color};">${area.suggestedRating}</div>
                <div style="font-size: 10px; color: ${color}; font-weight: 600; margin-top: 3px;">${area.level}</div>
              </div>
            </div>
            <p style="font-size: 13px; color: #4a3660; line-height: 1.6; margin: 0 0 10px;"><strong>Assessment:</strong> ${area.reasoning}</p>
            ${area.trajectory ? `<p style="font-size: 13px; color: #4a3660; line-height: 1.6; margin: 0 0 10px;"><strong>Trajectory:</strong> ${area.trajectory}</p>` : ''}
            ${area.growthAreas?.length ? `<p style="font-size: 13px; color: #4a3660; margin: 0 0 8px;"><strong>Growth Areas:</strong> ${area.growthAreas.join(', ')}</p>` : ''}
            ${area.suggestions?.length ? `
              <div style="margin-top: 8px;">
                <strong style="font-size: 13px; color: #3a2a50;">Suggestions:</strong>
                ${area.suggestions.map(s => `
                  <div style="margin: 6px 0; padding: 8px 12px; border-radius: 8px; background: white; border: 1px solid #e8e0f0;">
                    <div style="font-size: 12px; font-weight: 600; color: #3a2a50;">${s.action}</div>
                    <div style="font-size: 11px; color: #7a6a8a; margin-top: 2px;">Timeline: ${s.timeline}</div>
                    ${s.why ? `<div style="font-size: 11px; color: #5a4a6a; margin-top: 3px; font-style: italic;">${s.why}</div>` : ''}
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
        `;
      }).join('');

      // Get canvas as image
      let chartImg = '';
      if (canvasRef.current) {
        chartImg = canvasRef.current.toDataURL('image/png');
      }

      const htmlContent = `
        <!DOCTYPE html>
        <html><head>
          <meta charset="UTF-8">
          <title>Growth Assessment — ${reportYear}</title>
          <style>
            @page { margin: 0.75in; size: letter; }
            body { font-family: Georgia, 'Times New Roman', serif; color: #2d1b4e; line-height: 1.5; }
            h1 { font-size: 22px; color: #3a2a50; margin-bottom: 4px; }
            h2 { font-size: 18px; color: #3a2a50; margin: 24px 0 12px; border-bottom: 2px solid #e8e0f0; padding-bottom: 6px; }
            .subtitle { font-size: 13px; color: #7a6a8a; margin-bottom: 20px; }
            .disclaimer { padding: 10px 14px; border-radius: 8px; background: #fef3cd; border: 1px solid #f0e0a0; font-size: 11px; color: #92400e; margin-bottom: 24px; }
            .summary { font-size: 14px; color: #4a3660; line-height: 1.7; white-space: pre-wrap; margin-bottom: 24px; padding: 16px; background: #faf8ff; border-radius: 12px; }
            .chart-container { text-align: center; margin: 16px 0 24px; }
            .chart-container img { max-width: 380px; }
            .footer { margin-top: 30px; padding-top: 12px; border-top: 1px solid #e8e0f0; font-size: 10px; color: #9a8ab0; text-align: center; }
          </style>
        </head><body>
          <h1>🌱 Growth Assessment — ${reportYear}</h1>
          <div class="subtitle">Ordination Assessment of Readiness (OAR) Rubric Preview</div>
          <div class="disclaimer">⚠️ This is an AI-generated preview based on self-reported data. It is not a prediction of your official OAR rating and should be used as a reflective tool to guide your growth.</div>
          ${chartImg ? `<div class="chart-container"><img src="${chartImg}" alt="Radar Chart" /></div>` : ''}
          ${assessment.overallSummary ? `<h2>📝 Overall Summary</h2><div class="summary">${assessment.overallSummary}</div>` : ''}
          <h2>📊 Detailed Assessment</h2>
          ${areasHtml}
          <div class="footer">Generated by CMR Growth Dashboard • ${new Date().toLocaleDateString()}</div>
        </body></html>
      `;

      // Open print window for PDF
      const printWindow = window.open('', '_blank', 'width=800,height=1000');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        // Wait for images to load before printing
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 300);
        };
      }
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setPdfGenerating(false);
    }
  }, [assessment, reportYear]);

  return (
    <div>
      {/* Header */}
      <div style={{ ...cardStyle, marginBottom: 16, background: 'linear-gradient(135deg, rgba(124,77,186,0.06), rgba(200,160,240,0.10))' }}>
        <h3 style={{ margin: '0 0 6px', color: '#3a2a50', fontSize: 20, fontFamily: "'Georgia', serif" }}>
          🌱 Growth & Assessment
        </h3>
        <p style={{ margin: '0 0 16px', color: '#7a6a8a', fontSize: 13, lineHeight: 1.6, maxWidth: 600 }}>
          Preview how your report data maps to the 7 areas of the Ordination Assessment of Readiness (OAR) rubric.
          AI will analyze your responses {reportYear ? `for ${reportYear}` : ''} and suggest where you are on the developmental journey.
        </p>

        {/* Disclaimer */}
        <div style={{
          padding: '10px 14px', borderRadius: 12, marginBottom: 16,
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
          fontSize: 12, color: '#92400e', lineHeight: 1.5,
        }}>
          ⚠️ <strong>Important:</strong> This is an AI-generated preview based on your self-reported data.
          It is <strong>not</strong> a prediction of your official OAR rating and should be used as a reflective tool to guide your growth.
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => handleRunAssessment(true)}
            disabled={loading}
            style={{ ...btnPrimary, opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' }}
          >
            {loading ? (
              <>
                <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                Analyzing your report data...
              </>
            ) : assessment ? (
              <><span>🔄</span> Re-run Assessment</>
            ) : loadingCached ? (
              <><span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span> Loading...</>
            ) : (
              <><span>✨</span> Preview My Growth</>
            )}
          </button>

          {assessment && (
            <button
              onClick={handleDownloadPDF}
              disabled={pdfGenerating}
              style={{
                padding: '14px 24px', borderRadius: 22, border: '1.5px solid rgba(124,77,186,0.25)',
                cursor: 'pointer', background: 'rgba(255,255,255,0.6)', color: '#7c4dba',
                fontSize: 14, fontWeight: 600, transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(124,77,186,0.08)'; e.currentTarget.style.borderColor = '#7c4dba'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(124,77,186,0.25)'; }}
            >
              {pdfGenerating ? '⏳' : '📄'} Download PDF
            </button>
          )}

          {isCached && assessment && (
            <span style={{ fontSize: 11, color: '#9a8ab0', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 4 }}>
              💾 Loaded from saved assessment
            </span>
          )}
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

        {error && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 12, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)', color: '#991b1b', fontSize: 13 }}>
            ❌ {error}
          </div>
        )}
      </div>

      {/* Results */}
      {assessment && (
        <>
          {/* Radar Chart — full width above scores */}
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <canvas ref={canvasRef} style={{ display: 'block', marginBottom: 20 }} />
            </div>

            {/* Quick scores — below the chart */}
            <h4 style={{ margin: '0 0 12px', color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>
              Assessment Overview
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {RUBRIC_AREAS.map(ra => {
                const found = assessment.areas?.find(a => a.areaId === ra.id);
                if (!found) return null;
                const pct = (found.suggestedRating / 5) * 100;
                return (
                  <button
                    key={ra.id}
                    onClick={() => setExpandedArea(expandedArea === ra.id ? null : ra.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderRadius: 14, border: expandedArea === ra.id ? `2px solid ${ra.color}` : '1.5px solid rgba(180,160,210,0.12)',
                      background: expandedArea === ra.id ? `${ra.color}08` : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left', width: '100%',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: `${ra.color}15`, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 12, fontWeight: 700, color: ra.color,
                      flexShrink: 0,
                    }}>
                      {found.suggestedRating}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#3a2a50' }}>{ra.name}</div>
                      <div style={{ position: 'relative', height: 4, borderRadius: 2, background: 'rgba(180,160,210,0.12)', marginTop: 3 }}>
                        <div style={{
                          position: 'absolute', top: 0, left: 0, height: '100%', borderRadius: 2,
                          width: `${pct}%`, background: `linear-gradient(90deg, ${ra.color}80, ${ra.color})`,
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: ratingColor(found.suggestedRating), fontWeight: 600, flexShrink: 0 }}>
                      {found.level}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Overall Summary */}
          {assessment.overallSummary && (
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 10px', color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>
                📝 Overall Summary
              </h4>
              <div style={{ fontSize: 14, color: '#4a3660', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {assessment.overallSummary}
              </div>
            </div>
          )}

          {/* Expanded Area Detail */}
          {expandedArea && (() => {
            const ra = RUBRIC_AREAS.find(r => r.id === expandedArea);
            const area = assessment.areas?.find(a => a.areaId === expandedArea);
            if (!ra || !area) return null;

            return (
              <div style={{
                ...cardStyle, marginBottom: 16,
                borderLeft: `4px solid ${ra.color}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px', color: '#3a2a50', fontSize: 18, fontFamily: "'Georgia', serif" }}>
                      {ra.name}
                    </h4>
                    <p style={{ margin: 0, fontSize: 12, color: '#7a6a8a', fontStyle: 'italic' }}>
                      {ra.movement}
                    </p>
                  </div>
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: `linear-gradient(135deg, ${ra.color}15, ${ra.color}25)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, fontWeight: 700, color: ra.color,
                    }}>
                      {area.suggestedRating}
                    </div>
                    <div style={{ fontSize: 10, color: ratingColor(area.suggestedRating), fontWeight: 600, marginTop: 4 }}>
                      {area.level}
                    </div>
                  </div>
                </div>

                {/* Level spectrum visualization */}
                <div style={{
                  display: 'flex', gap: 0, marginBottom: 16, borderRadius: 10, overflow: 'hidden',
                  border: '1px solid rgba(180,160,210,0.12)',
                }}>
                  {ra.levels.map((lbl, i) => {
                    const ratings = i === 0 ? [1, 2] : i === 1 ? [3, 4] : [5];
                    const isActive = ratings.includes(area.suggestedRating);
                    return (
                      <div key={i} style={{
                        flex: i < 2 ? 2 : 1, padding: '8px 12px', textAlign: 'center',
                        background: isActive ? `${ra.color}12` : 'rgba(250,248,255,0.5)',
                        borderRight: i < 2 ? '1px solid rgba(180,160,210,0.12)' : undefined,
                      }}>
                        <div style={{
                          fontSize: 10, fontWeight: 600,
                          color: isActive ? ra.color : '#9a8ab0',
                        }}>
                          {isActive ? '● ' : ''}{lbl}
                        </div>
                        <div style={{ fontSize: 9, color: '#b0a0c0', marginTop: 2 }}>
                          Rating {ratings.join(' & ')}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Reasoning */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#3a2a50', marginBottom: 6 }}>
                    📊 Assessment Reasoning
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: '#4a3660', lineHeight: 1.6 }}>
                    {area.reasoning}
                  </p>
                </div>

                {/* Trajectory */}
                {area.trajectory && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3a2a50', marginBottom: 6 }}>
                      📈 Growth Trajectory
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#4a3660', lineHeight: 1.6 }}>
                      {area.trajectory}
                    </p>
                  </div>
                )}

                {/* Growth Areas */}
                {area.growthAreas?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3a2a50', marginBottom: 6 }}>
                      🌱 Growth Areas
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {area.growthAreas.map((g, i) => (
                        <span key={i} style={{
                          padding: '5px 12px', borderRadius: 10,
                          background: `${ra.color}08`, border: `1px solid ${ra.color}20`,
                          fontSize: 12, color: ra.color, fontWeight: 500,
                        }}>
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {area.suggestions?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3a2a50', marginBottom: 8 }}>
                      💡 Suggestions to Move Forward
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {area.suggestions.map((s, i) => (
                        <div key={i} style={{
                          padding: '12px 16px', borderRadius: 14,
                          background: 'rgba(255,255,255,0.6)',
                          border: '1px solid rgba(180,160,210,0.1)',
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#3a2a50', marginBottom: 4 }}>
                            {s.action}
                          </div>
                          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#7a6a8a' }}>
                            <span>⏱️ {s.timeline}</span>
                          </div>
                          {s.why && (
                            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#5a4a6a', lineHeight: 1.5, fontStyle: 'italic' }}>
                              {s.why}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* All areas detail cards (collapsed list) */}
          {!expandedArea && (
            <div style={{ fontSize: 12, color: '#9a8ab0', textAlign: 'center', padding: '8px 0' }}>
              👆 Click any area above to see detailed reasoning, growth areas, and suggestions
            </div>
          )}
        </>
      )}

      {/* Pre-assessment: show the 7 areas as an overview */}
      {!assessment && !loading && (
        <div style={{ ...cardStyle }}>
          <h4 style={{ margin: '0 0 12px', color: '#3a2a50', fontSize: 16, fontFamily: "'Georgia', serif" }}>
            The 7 Assessment Areas
          </h4>
          <p style={{ margin: '0 0 16px', color: '#7a6a8a', fontSize: 13 }}>
            Your report data will be analyzed across these dimensions of ministerial development:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {RUBRIC_AREAS.map(ra => (
              <div key={ra.id} style={{
                padding: '14px 16px', borderRadius: 14,
                background: `${ra.color}06`, border: `1px solid ${ra.color}15`,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: `${ra.color}12`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 13, fontWeight: 700, color: ra.color,
                  flexShrink: 0,
                }}>
                  {ra.number}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#3a2a50' }}>{ra.name}</div>
                  <div style={{ fontSize: 11, color: '#7a6a8a', marginTop: 2, lineHeight: 1.4 }}>
                    {ra.levels[0]} → {ra.levels[1]} → {ra.levels[2]}
                  </div>
                  <div style={{ fontSize: 11, color: '#9a8ab0', marginTop: 2, fontStyle: 'italic' }}>
                    {ra.movement}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
